import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NON_ADMIN_ROLE, useCachedTags, useRolesQuery, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { type MultiSelectOption } from "@tellescope/ui";
import { MessageTemplateType } from "@tellescope/types-models";
import { deepEqual } from "@tellescope/utils";
import {
  CHANNEL_VALUES,
  countActiveTemplateFilters,
  TEMPLATE_TYPE_VALUES,
  type TemplatesFilters,
} from "./templatesFilters";

export interface UseTemplatesFilterDialogArgs {
  open: boolean;
  filters: TemplatesFilters;
  onApply: (filters: TemplatesFilters) => void;
  onOpenChange: (open: boolean) => void;
}

export const useTemplatesFilterDialog = ({ open, filters, onApply, onOpenChange }: UseTemplatesFilterDialogArgs) => {
  const { t } = useTranslation("templates");

  const [staged, setStaged] = useState<TemplatesFilters>(filters);
  const [wasOpen, setWasOpen] = useState(open);
  if (open && !wasOpen) {
    setWasOpen(true);
    setStaged(filters);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  const setField = useCallback(<K extends keyof TemplatesFilters>(key: K, value: TemplatesFilters[K]) => {
    setStaged((prev) => ({ ...prev, [key]: value }));
  }, []);

  const { roles: roleRecords, isLoading: rolesLoading } = useRolesQuery({
    select: (roles) =>
      roles.map(({ role }) => ({ value: role, label: role === NON_ADMIN_ROLE ? t("Non-Admin") : role })),
  });

  const entitiesQuery = useTellescopeInfiniteQuery({
    model: "enduser_custom_types",
    method: "getSome",
    request: { limit: 500 },
    select: (data) => data.pages.flat(),
  });

  const { data: userTags = [] } = useCachedTags({ model: "users" });
  const { data: templateTags = [] } = useCachedTags({ model: "templates" });

  const typeLabels = useMemo<Record<MessageTemplateType, string>>(
    () => ({ enduser: t("Patient"), Reply: t("Reply"), team: t("Team") }),
    [t]
  );

  const typeOptions = useMemo<MultiSelectOption<MessageTemplateType>[]>(
    () => TEMPLATE_TYPE_VALUES.map((value) => ({ value, label: typeLabels[value] })),
    [typeLabels]
  );

  const channelOptions = useMemo<MultiSelectOption[]>(
    () => CHANNEL_VALUES.map((value) => ({ value, label: value })),
    []
  );

  const entityOptions = useMemo<MultiSelectOption[]>(
    () => (entitiesQuery.data ?? []).map((entity) => ({ value: entity.id, label: entity.title })),
    [entitiesQuery.data]
  );

  const userTagOptions = useMemo<MultiSelectOption[]>(
    () => userTags.map((tag) => ({ value: tag, label: tag })),
    [userTags]
  );

  const tagOptions = useMemo<MultiSelectOption[]>(
    () => templateTags.map((tag) => ({ value: tag, label: tag })),
    [templateTags]
  );

  const handleApply = useCallback(() => {
    onApply(staged);
    onOpenChange(false);
  }, [onApply, onOpenChange, staged]);

  const handleClearAll = useCallback(() => setStaged({}), []);

  const canApply = !deepEqual(staged, filters, { includeRemovals: true });
  const canClear = countActiveTemplateFilters(staged) > 0;

  return {
    staged,
    setField,
    typeOptions,
    channelOptions,
    roleOptions: roleRecords,
    entityOptions,
    userTagOptions,
    tagOptions,
    rolesLoading,
    entitiesLoading: entitiesQuery.isLoading,
    handleApply,
    handleClearAll,
    canApply,
    canClear,
  };
};
